<template>
  <div
    ref="container"
    v-editable="blok"
    class="cm-stage"
    :class="['container-full', `is-${blok.theme}`, { 'header-has-tabs': headerState.hasTabs }]">
    <!-- SEO Headline (visually hidden, but holds the combined headline) -->
    <h1 class="seo-headline">{{ seoHeadline }}</h1>

    <!-- Back button -->
    <UtilLinkItemIcon
      v-if="blok.breadcrumb"
      :blok="{
        component: 'util_link-overlay-item',
        type: 'link',
        link: props.blok.breadcrumb_link,
        text: props.blok.breadcrumb ?? '',
        _uid: `breadcrumb_${props.blok.breadcrumb}`,
      }"
      :isDark="blok.theme === 'red'"
      class="back-button"
      forceIcon="fi_arrow-left"
      forceIconPosition="start" />

    <div
      class="content"
      :class="{
        'has-media-small': smallMedia?.main || smallMedia?.poster,
        'has-media-large': hasLargeMedia,
      }">
      <!-- Headlines -->
      <span ref="headline" class="heading-main-xlarge headline" aria-hidden="true"></span>
      <span
        v-if="blok.headline_highlight"
        ref="headline-highlight"
        class="heading-main-xlarge headline-highlight"
        aria-hidden="true"></span>

      <!-- Content (Copy + Button) -->
      <div class="text-container">
        <p v-if="blok.text" class="text text-medium-regular">
          {{ blok.text }}
        </p>

        <UtilButton
          v-if="blok.button_text"
          class="button"
          :blok="{
            component: 'util_button',
            text: blok.button_text,
            link: blok.button_link,
            theme: 'primary',
            size: breakpointState.isTabletLarge ? 'large' : 'small',
            _uid: 'button',
          }"
          :forceIcon="hasExternalLink ? 'fi_arrow-right-1' : undefined"
          :isBackgroundDark="blok.theme === 'red'" />
      </div>

      <!-- Media -->
      <div v-if="hasLargeMedia" class="media-large">
        <UtilMediaResponsive
          v-if="blok.media_large_type === 'media'"
          :image="
            blok.media_large_desktop_poster?.filename ? blok.media_large_desktop_poster : blok.media_large_desktop
          "
          :imageMobile="
            blok.media_large_mobile_poster?.filename ? blok.media_large_mobile_poster : blok.media_large_mobile
          "
          :video="blok.media_large_desktop"
          :videoMobile="blok.media_large_mobile"
          :autoplay="true"
          :aspectRatio="isMobile ? '3/4' : '1/1'"
          :breakpointsWidthMap="breakpointsWidthMediaLargeMap"
          :isDark="false"
          playButtonTheme="secondary"
          :wave="blok.wave_media_large" />
        <UtilYoutubeVideo
          v-else-if="blok.media_large_type === 'youtube-video' && blok.media_large_youtube_video?.[0] && settings"
          :blok="{ ...blok.media_large_youtube_video[0], media_ratio: isMobile ? '3/4' : '1/1' }"
          :privacyWarningRichtext="settings?.youtube_privacy_warning"
          :breakpointsWidthMap="breakpointsWidthMediaLargeMap"
          isShownInModal
          :isDark="false"
          playButtonTheme="secondary" />
      </div>

      <div v-if="blok.media_small?.filename" class="media-small">
        <!-- Wave -->
        <UtilWave
          v-if="blok.wave_media_small?.length"
          v-editable="blok.wave_media_small[0]"
          :blok="blok.wave_media_small[0]" />

        <SharedBaseVideo
          v-if="smallMedia?.main && isVideo(smallMedia.main.filename)"
          :src="smallMedia.main.filename"
          :poster="smallMedia.poster?.filename"
          :autoplay="true"
          :loop="true"
          :posterBreakPointsWidthMap="breakpointsWidthMediaSmallMap" />
        <SharedBaseImage v-else :image="smallMedia?.main" :breakpointsWidthMap="breakpointsWidthMediaSmallMap" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useHeader } from '#imports';
import { gsap } from 'gsap';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { SharedBaseImage, SharedBaseVideo } from '@seb-platform/shared/components/base';
import { useBreakpoint, useTransition } from '@seb-platform/shared/composables';
import { isVideo } from '@seb-platform/shared/utils/media';
import { urlIsExternal } from '@seb-platform/shared/utils/url';
import UtilButton from '~/components/storyblok/utils/UtilButton/UtilButton.vue';
import UtilLinkItemIcon from '~/components/storyblok/utils/UtilLinkItemIcon/UtilLinkItemIcon.vue';
import UtilMediaResponsive from '~/components/storyblok/utils/UtilMediaResponsive/UtilMediaResponsive.vue';
import UtilWave from '~/components/storyblok/utils/UtilWave/UtilWave.vue';
import type { CmStageStoryblok, CoreSettingsStoryblok } from '~/types/storyblok-generated';
import type { ModuleWrapperState } from '~/types/utils';
import UtilYoutubeVideo from '../../utils/UtilYoutubeVideo/UtilYoutubeVideo.vue';

const props = defineProps<{
  blok: CmStageStoryblok;
  state: ModuleWrapperState;
  settings?: CoreSettingsStoryblok;
}>();

const { fadeSlide } = useTransition();
const { state: breakpointState } = useBreakpoint();
const { state: headerState } = useHeader();

const $container = useTemplateRef('container');
const $headline = useTemplateRef('headline');
const $headlineHighlight = useTemplateRef('headline-highlight');
const isVisible = ref(false);
const hasExternalLink = computed(
  () => props.blok.button_link?.linktype === 'url' && urlIsExternal(props.blok.button_link?.url ?? '')
);
const isMobile = computed(() => !breakpointState.isPhoneLarge);
const seoHeadline = computed(() =>
  [props.blok.headline, props.blok.headline_highlight ?? ''].join(' ').replace(/&shy;/g, '')
);

const hasLargeMedia = computed<boolean>(() => {
  if (props.blok.media_large_type === 'youtube-video') {
    return !!props.blok.media_large_youtube_video?.[0];
  } else {
    return !!props.blok.media_large_desktop?.filename;
  }
});

const smallMedia = computed(() => {
  if (!hasLargeMedia.value) {
    // The small media is not shown if no large media is set
    return { main: undefined, poster: undefined };
  }
  return {
    main: props.blok.media_small?.filename ? props.blok.media_small : undefined,
    poster: props.blok.media_small_poster?.filename ? props.blok.media_small_poster : undefined,
  };
});

const breakpointsWidthMediaSmallMap = {
  '0': 140,
  '375': 290,
  '768': 224,
  '1024': 283,
  '1280': 307,
  '1440': 418,
  '2440': 543,
};
const breakpointsWidthMediaLargeMap = {
  '0': 198,
  '375': 409,
  '768': 463,
  '1024': 581,
  '1280': 638,
  '1440': 859,
  '2440': 1110,
};

function fadeInElements() {
  if (!$container.value) {
    return;
  }

  // Transition composable is taking over
  gsap.set($container.value, { autoAlpha: 1 });

  // Grab all elements
  const els = Array.from(
    $container.value?.querySelectorAll<HTMLDivElement>(
      '.media-small, .media-large, .headline, .headline-highlight, .text-container, .button'
    ) as NodeListOf<HTMLElement>
  )
    // Sort elements by their position (from top to bottom)
    .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

  // Merging elements
  fadeSlide([...els]);
}

function onProgress() {
  if (!isVisible.value) {
    fadeInElements();
    isVisible.value = true;
  }
}

watch(() => props.state.progress, onProgress);

/**
 * This sets the content of the visible headline after the initial render (onMounted).
 * This is important because we have a "SEO headline" element that holds the headline in one piece (semantically correct).
 * The visible headlines are <span> elements and use the area hidden attribute in order to hide them from search engines.
 */
function setHeadlineContents() {
  if ($headline.value) {
    $headline.value.innerHTML = props.blok.headline;
  }
  if ($headlineHighlight.value && props.blok.headline_highlight) {
    $headlineHighlight.value.innerText = props.blok.headline_highlight;
  }
}

onMounted(async () => {
  setHeadlineContents();

  await nextTick();
  if (props.state.progress) {
    onProgress();
  }
});
</script>

<style src="./CMStage.scss" lang="scss" scoped />
